import { HStack, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import type React from "react";
import EntityModalButton, { EntityModalButtonGroup } from "../../components/EntityModalButton";
import { getEntityCalendarMonthName } from "./entityProfileCalendar.utils";

type Props = {
  actionButtons?: React.ReactNode;
  monthDate: LocalDate;
  onChange: (monthDate: LocalDate) => void;
};

const monthValues = Array.from({ length: 12 }, (_, index) => index + 1);

/**
 * Action buttons stay on a single row like their Angular counterparts: when the
 * container gets narrow (e.g. the new visit side modal opens) they slide toward
 * the month/year pickers and the leftovers are clipped instead of wrapping onto
 * a new row. The overflow stays scrollable so clipped buttons remain reachable,
 * but its scrollbar is hidden so the row keeps its height.
 */
export const clippedActionsScrollSx = {
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
};

const menuListStyles = {
  borderColor: "#ccc",
  borderRadius: "4px",
  boxShadow: "0 6px 12px rgba(0, 0, 0, 0.175)",
  p: "5px",
};

function getMenuItemStyles(isSelected: boolean) {
  return {
    _active: {
      bg: isSelected ? "#5a55f5" : "#e8e8e8",
      color: isSelected ? "#fff" : "#262626",
    },
    _focus: {
      bg: isSelected ? "#5a55f5" : "#f5f5f5",
      color: isSelected ? "#fff" : "#262626",
    },
    _hover: {
      bg: isSelected ? "#5a55f5" : "#f5f5f5",
      color: isSelected ? "#fff" : "#262626",
    },
    bg: isSelected ? "#5a55f5" : "#fff",
    borderRadius: "3px",
    color: isSelected ? "#fff" : "#333",
    fontSize: "14px",
    minH: "32px",
    px: "12px",
  };
}

function getYearOptions() {
  const currentYear = LocalDate.now().year();

  return Array.from({ length: 5 }, (_, index) => currentYear + 1 - index);
}

function setMonth(monthDate: LocalDate, month: number) {
  return LocalDate.of(monthDate.year(), month, 1);
}

function setYear(monthDate: LocalDate, year: number) {
  return LocalDate.of(year, monthDate.monthValue(), 1);
}

export default function EntityProfileCalendarControls(props: Props) {
  const yearOptions = getYearOptions();

  return (
    <HStack alignItems="flex-start" flexWrap="nowrap" justifyContent="space-between" spacing={3}>
      <HStack flexShrink={0} flexWrap="nowrap" spacing={3}>
        <EntityModalButton kind="white" size="sm" onClick={() => props.onChange(LocalDate.now())}>
          Today
        </EntityModalButton>
        <EntityModalButtonGroup>
          <EntityModalButton
            aria-label="Previous month"
            iconClassName="fa fa-chevron-left"
            kind="white"
            size="sm"
            onClick={() => props.onChange(props.monthDate.minusMonths(1))}
          />
          <Menu isLazy lazyBehavior="unmount" placement="bottom-start">
            {/* Chakra owns the trigger element, so it carries the bootstrap
                classes directly instead of rendering an EntityModalButton. */}
            <MenuButton className="btn btn-sm btn-default" style={{ minWidth: 100 }}>
              <Text fontWeight={400}>
                {getEntityCalendarMonthName(props.monthDate.monthValue())}
              </Text>
            </MenuButton>
            <MenuList minW="158px" {...menuListStyles}>
              {monthValues.map((month) => (
                <MenuItem
                  key={month}
                  {...getMenuItemStyles(month === props.monthDate.monthValue())}
                  onClick={() => props.onChange(setMonth(props.monthDate, month))}
                >
                  {getEntityCalendarMonthName(month)}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <EntityModalButton
            aria-label="Next month"
            iconClassName="fa fa-chevron-right"
            kind="white"
            size="sm"
            onClick={() => props.onChange(props.monthDate.plusMonths(1))}
          />
        </EntityModalButtonGroup>
        <EntityModalButtonGroup>
          <EntityModalButton
            aria-label="Previous year"
            iconClassName="fa fa-chevron-left"
            kind="white"
            size="sm"
            onClick={() => props.onChange(props.monthDate.minusYears(1))}
          />
          <Menu isLazy lazyBehavior="unmount" placement="bottom-start">
            <MenuButton className="btn btn-sm btn-default" style={{ minWidth: 64 }}>
              <Text fontWeight={400}>{props.monthDate.year()}</Text>
            </MenuButton>
            <MenuList minW="104px" {...menuListStyles}>
              {yearOptions.map((year) => (
                <MenuItem
                  key={year}
                  {...getMenuItemStyles(year === props.monthDate.year())}
                  onClick={() => props.onChange(setYear(props.monthDate, year))}
                >
                  {year}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <EntityModalButton
            aria-label="Next year"
            iconClassName="fa fa-chevron-right"
            kind="white"
            size="sm"
            onClick={() => props.onChange(props.monthDate.plusYears(1))}
          />
        </EntityModalButtonGroup>
      </HStack>
      {props.actionButtons ? (
        <HStack flexWrap="nowrap" minW={0} overflowX="auto" spacing={3} sx={clippedActionsScrollSx}>
          {props.actionButtons}
        </HStack>
      ) : null}
    </HStack>
  );
}
